import { useMemo, useState } from "react";
import { Card, Radio } from "antd";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const DATE_FMT = "YYYY-MM-DDTHH:mm";

const parseDate = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const breakdownCalendar = (start, end) => {
  let a = start;
  const b = end;
  const years = b.diff(a, "year");
  a = a.add(years, "year");
  const months = b.diff(a, "month");
  a = a.add(months, "month");
  const days = b.diff(a, "day");
  a = a.add(days, "day");
  const hours = b.diff(a, "hour");
  a = a.add(hours, "hour");
  const minutes = b.diff(a, "minute");
  a = a.add(minutes, "minute");
  const seconds = b.diff(a, "second");
  return { years, months, days, hours, minutes, seconds };
};

const formatIso8601Duration = (b) => {
  if (!b) return "";
  const datePart = [
    b.years ? `${b.years}Y` : "",
    b.months ? `${b.months}M` : "",
    b.days ? `${b.days}D` : "",
  ].join("");
  const timePart = [
    b.hours ? `${b.hours}H` : "",
    b.minutes ? `${b.minutes}M` : "",
    b.seconds ? `${b.seconds}S` : "",
  ].join("");
  if (!datePart && !timePart) return "PT0S";
  return timePart ? `P${datePart}T${timePart}` : `P${datePart}`;
};

const countBusinessDays = (start, end) => {
  let s = start.startOf("day");
  const e = end.startOf("day");
  if (s.isSame(e)) return 0;
  const totalDays = e.diff(s, "day");
  if (Math.abs(totalDays) > 200000) return null;
  const sign = totalDays < 0 ? -1 : 1;
  let count = 0;
  const step = sign;
  const limit = Math.abs(totalDays);
  let cursor = s;
  for (let i = 0; i < limit; i += 1) {
    cursor = cursor.add(step, "day");
    const dow = cursor.day();
    if (dow !== 0 && dow !== 6) count += 1;
  }
  return sign * count;
};

const OFFSET_UNITS = ["day", "week", "month", "year", "hour", "minute"];

const DateDifferenceCalculator = () => {
  const { t } = useTranslation();
  const [startInput, setStartInput] = useState(() =>
    dayjs().startOf("day").format(DATE_FMT),
  );
  const [endInput, setEndInput] = useState(() =>
    dayjs().add(30, "day").startOf("day").format(DATE_FMT),
  );
  const [baseInput, setBaseInput] = useState(() =>
    dayjs().startOf("day").format(DATE_FMT),
  );
  const [offsetValue, setOffsetValue] = useState("14");
  const [offsetUnit, setOffsetUnit] = useState("day");
  const [offsetDir, setOffsetDir] = useState("add");

  const start = useMemo(() => parseDate(startInput), [startInput]);
  const end = useMemo(() => parseDate(endInput), [endInput]);

  const direction = useMemo(() => {
    if (!start || !end) return 0;
    if (end.valueOf() > start.valueOf()) return 1;
    if (end.valueOf() < start.valueOf()) return -1;
    return 0;
  }, [start, end]);

  const calendar = useMemo(() => {
    if (!start || !end || direction === 0) return null;
    return direction === 1
      ? breakdownCalendar(start, end)
      : breakdownCalendar(end, start);
  }, [start, end, direction]);

  const totals = useMemo(() => {
    if (!start || !end) return null;
    const ms = end.valueOf() - start.valueOf();
    const abs = Math.abs(ms);
    return {
      seconds: Math.floor(abs / 1000),
      minutes: Math.floor(abs / (1000 * 60)),
      hours: Math.floor(abs / (1000 * 60 * 60)),
      days: Math.floor(abs / (1000 * 60 * 60 * 24)),
      weeks: Math.floor(abs / (1000 * 60 * 60 * 24 * 7)),
    };
  }, [start, end]);

  const businessDays = useMemo(() => {
    if (!start || !end) return null;
    return countBusinessDays(start, end);
  }, [start, end]);

  const base = useMemo(() => parseDate(baseInput), [baseInput]);
  const offsetNum = useMemo(() => {
    const n = Number(offsetValue);
    return Number.isFinite(n) ? n : null;
  }, [offsetValue]);

  const offsetResult = useMemo(() => {
    if (!base || offsetNum === null) return null;
    const signed = offsetDir === "subtract" ? -offsetNum : offsetNum;
    return base.add(signed, offsetUnit);
  }, [base, offsetNum, offsetDir, offsetUnit]);

  const copy = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const swap = () => {
    setStartInput(endInput);
    setEndInput(startInput);
  };

  const useNowStart = () => setStartInput(dayjs().format(DATE_FMT));
  const useNowEnd = () => setEndInput(dayjs().format(DATE_FMT));

  const formatCalendar = (b) => {
    if (!b) return "";
    const parts = [];
    if (b.years) parts.push(`${b.years} ${t(b.years === 1 ? "date_diff_unit_year" : "date_diff_unit_years")}`);
    if (b.months) parts.push(`${b.months} ${t(b.months === 1 ? "date_diff_unit_month" : "date_diff_unit_months")}`);
    if (b.days) parts.push(`${b.days} ${t(b.days === 1 ? "date_diff_unit_day" : "date_diff_unit_days")}`);
    if (b.hours) parts.push(`${b.hours} ${t(b.hours === 1 ? "date_diff_unit_hour" : "date_diff_unit_hours")}`);
    if (b.minutes) parts.push(`${b.minutes} ${t(b.minutes === 1 ? "date_diff_unit_minute" : "date_diff_unit_minutes")}`);
    if (b.seconds) parts.push(`${b.seconds} ${t(b.seconds === 1 ? "date_diff_unit_second" : "date_diff_unit_seconds")}`);
    return parts.length === 0
      ? `0 ${t("date_diff_unit_seconds")}`
      : parts.join(", ");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("date_diff_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("date_diff_intro")}</p>
            </div>

            <div className="flex flex-col gap-3">
              <h2 className="text-lg font-semibold text-gray-800">
                {t("date_diff_section_between_heading")}
              </h2>

              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="date-diff-start">
                    {t("date_diff_start_label")}
                  </label>
                  <input
                    id="date-diff-start"
                    type="datetime-local"
                    value={startInput}
                    onChange={(e) => setStartInput(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono"
                  />
                  <StandardButton
                    onClick={useNowStart}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("date_diff_use_now")}
                  </StandardButton>
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="date-diff-end">
                    {t("date_diff_end_label")}
                  </label>
                  <input
                    id="date-diff-end"
                    type="datetime-local"
                    value={endInput}
                    onChange={(e) => setEndInput(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono"
                  />
                  <StandardButton
                    onClick={useNowEnd}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("date_diff_use_now")}
                  </StandardButton>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <StandardButton
                  onClick={swap}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  disabled={!start || !end}
                >
                  {t("date_diff_swap")}
                </StandardButton>
              </div>

              {(startInput && !start) || (endInput && !end) ? (
                <p className="text-sm text-red-600">
                  {t("date_diff_invalid_date")}
                </p>
              ) : null}

              {start && end && (
                <>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 flex items-center justify-between gap-3">
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs uppercase tracking-wide text-gray-500">
                        {direction === -1
                          ? t("date_diff_direction_before")
                          : direction === 1
                            ? t("date_diff_direction_after")
                            : t("date_diff_direction_same")}
                      </span>
                      <span className="font-mono text-sm break-words text-gray-800">
                        {calendar ? formatCalendar(calendar) : t("date_diff_zero")}
                      </span>
                    </div>
                    <StandardButton
                      onClick={() =>
                        copy(formatCalendar(calendar), t("date_diff_breakdown_label"))
                      }
                      type="secondary"
                      variant="outlined"
                      size="small"
                      disabled={!calendar}
                    >
                      {t("copy_button")}
                    </StandardButton>
                  </div>

                  <ul className="flex flex-col divide-y divide-indigo-100 bg-white border border-indigo-100 rounded-md">
                    {totals &&
                      [
                        {
                          key: "iso8601",
                          labelKey: "date_diff_total_iso8601",
                          value: calendar ? formatIso8601Duration(calendar) : "PT0S",
                        },
                        { key: "weeks", labelKey: "date_diff_total_weeks", value: totals.weeks },
                        { key: "days", labelKey: "date_diff_total_days", value: totals.days },
                        { key: "hours", labelKey: "date_diff_total_hours", value: totals.hours },
                        { key: "minutes", labelKey: "date_diff_total_minutes", value: totals.minutes },
                        { key: "seconds", labelKey: "date_diff_total_seconds", value: totals.seconds },
                        {
                          key: "businessDays",
                          labelKey: "date_diff_total_business_days",
                          value: businessDays === null ? "—" : Math.abs(businessDays),
                        },
                      ].map((row) => (
                        <li
                          key={row.key}
                          className="flex items-center justify-between gap-3 px-3 py-2"
                        >
                          <div className="flex flex-col">
                            <span className="text-xs uppercase tracking-wide text-gray-500">
                              {t(row.labelKey)}
                            </span>
                            <span className="font-mono text-sm break-all text-gray-800">
                              {typeof row.value === "number"
                                ? row.value.toLocaleString()
                                : row.value}
                            </span>
                          </div>
                          <StandardButton
                            onClick={() => copy(String(row.value), t(row.labelKey))}
                            type="secondary"
                            variant="outlined"
                            size="small"
                            disabled={row.value === "—"}
                          >
                            {t("copy_button")}
                          </StandardButton>
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </div>

            <div className="flex flex-col gap-3 pt-2 border-t border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">
                {t("date_diff_section_offset_heading")}
              </h2>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="date-diff-base">
                  {t("date_diff_base_label")}
                </label>
                <input
                  id="date-diff-base"
                  type="datetime-local"
                  value={baseInput}
                  onChange={(e) => setBaseInput(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono"
                />
              </div>

              <div className="flex flex-col gap-2 md:flex-row md:items-end">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("date_diff_offset_direction_label")}
                  </label>
                  <Radio.Group
                    value={offsetDir}
                    onChange={(e) => setOffsetDir(e.target.value)}
                  >
                    <Radio.Button value="add">{t("date_diff_offset_add")}</Radio.Button>
                    <Radio.Button value="subtract">
                      {t("date_diff_offset_subtract")}
                    </Radio.Button>
                  </Radio.Group>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700" htmlFor="date-diff-offset-value">
                    {t("date_diff_offset_value_label")}
                  </label>
                  <input
                    id="date-diff-offset-value"
                    type="number"
                    min="0"
                    value={offsetValue}
                    onChange={(e) => setOffsetValue(e.target.value)}
                    className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("date_diff_offset_unit_label")}
                  </label>
                  <Radio.Group
                    value={offsetUnit}
                    onChange={(e) => setOffsetUnit(e.target.value)}
                  >
                    {OFFSET_UNITS.map((u) => (
                      <Radio.Button key={u} value={u}>
                        {t(`date_diff_unit_${u}s`)}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </div>
              </div>

              {base && offsetResult && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 flex items-center justify-between gap-3">
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs uppercase tracking-wide text-gray-500">
                      {t("date_diff_offset_result_label")}
                    </span>
                    <span className="font-mono text-sm break-all text-gray-800">
                      {offsetResult.format("YYYY-MM-DD HH:mm:ss")}
                    </span>
                    <span className="text-xs text-gray-500 mt-1">
                      {offsetResult.format("dddd, MMMM D, YYYY")}
                    </span>
                  </div>
                  <StandardButton
                    onClick={() =>
                      copy(
                        offsetResult.format("YYYY-MM-DD HH:mm:ss"),
                        t("date_diff_offset_result_label"),
                      )
                    }
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("date_diff_note_calendar")}</p>
              <p>{t("date_diff_note_business")}</p>
              <p>{t("date_diff_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default DateDifferenceCalculator;
